import React from "react";
import Panel from "../../../common/components/panel";
import {
  ResourceMetadataFieldFragment,
  RmMappingDetailsFragment,
  RmMappingSourceFragment,
  useCanbySuggestionsQuery,
} from "../../../graphql/generated";
import LoadingIcon from "../../../common/components/loadingicon";
import {
  ResourceMetadataLookupValueComponent,
  resourceMetadataLookupValueForValue,
} from "../resource-metadata/resource-metadata-lookup-value-component";
import { sourceName } from "../mapping-source";
import Loading from "../../../common/components/loading";
import Error from "../../../common/components/error";
import { ExclamationCircleIcon } from "@heroicons/react/24/outline";
import { CheckCircleIcon } from "@heroicons/react/24/solid";
import { classNames } from "../../../common/utils/classnames";

const CanbySuggestions: React.FC<{
  mapping: RmMappingDetailsFragment;
  source: RmMappingSourceFragment;
  existingMappings: Set<string>;
  field?: ResourceMetadataFieldFragment;
  add: (source: string, destination: string) => Promise<boolean>;
}> = ({ mapping, source, existingMappings, field, add }) => {
  if (!mapping.field.rootResource.canbyAnalysis) {
    return <></>;
  }

  if (source.__typename != "RMFieldMappingSource") {
    return <></>;
  }

  const [{ data, error, fetching }] = useCanbySuggestionsQuery({
    variables: {
      canbyId: mapping.field.rootResource.canbyAnalysis.id,
      fieldName: source.name,
    },
  });

  const [isRunning, setIsRunning] = React.useState(false);
  const [current, setCurrent] = React.useState<string>();

  const items = React.useMemo(() => {
    if (!data?.canbyAnalysis.field.keywords) {
      return undefined;
    }

    return data.canbyAnalysis.field.keywords.map((item) => {
      const mappedValue = resourceMetadataLookupValueForValue(
        item.keyword,
        field,
      );
      return {
        source: item.keyword,
        destination: mappedValue?.name ?? item.keyword,
        fetching: item.keyword === current,
        exists: existingMappings.has(item.keyword),
        count: item.count,
      };
    });
  }, [data, field, existingMappings, current]);

  React.useEffect(() => {
    const work = async () => {
      if (items && isRunning && !current) {
        const first = items.find((item) => !item.exists);
        if (first) {
          try {
            setCurrent(first.source);
            const success = await add(first.source, first.destination);
            if (!success) {
              setIsRunning(false);
            }
          } catch (err) {
            setIsRunning(false);
          } finally {
            setCurrent(undefined);
          }
        } else {
          setIsRunning(false);
        }
      }
    };
    work();
    return;
  }, [isRunning, current, items]);

  const addAllButtonDisabled =
    isRunning || !items || items.every((item) => item.exists);

  return (
    <Panel>
      <Panel.Title>Canby Suggestions</Panel.Title>
      <Panel.Body>
        {fetching && <Loading show />}
        {error && <Error error={error} />}
        {items && (
          <table className="w-full text-sm text-left">
            <thead>
              <tr>
                <th className="p-1"></th>
                <th className="p-1">Source: {sourceName(source)}</th>
                <th className="p-1">Destination: {mapping.field.name}</th>
                <th className="p-1">Count</th>
              </tr>
            </thead>
            <tbody>
              {items.map((keyword) => (
                <tr key={keyword.source}>
                  <td>
                    <ExistsIcon
                      exists={keyword.exists}
                      fetching={keyword.fetching}
                    />
                  </td>
                  <td>
                    <ResourceMetadataLookupValueComponent
                      field={field}
                      value={keyword.source}
                    />
                  </td>
                  <td>{keyword.destination}</td>
                  <td>{keyword.count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Panel.Body>
      {items && (
        <Panel.Footer>
          <button
            className={classNames(
              addAllButtonDisabled
                ? "bg-gray-500 text-white"
                : "bg-zenlist-500 hover:bg-zenlist-700 text-white",
              "font-bold py-2 px-4 rounded",
            )}
            onClick={() => setIsRunning(true)}
            disabled={addAllButtonDisabled}
          >
            {isRunning ? (
              <>
                <LoadingIcon /> Adding...
              </>
            ) : (
              <>Add all</>
            )}
          </button>
        </Panel.Footer>
      )}
    </Panel>
  );
};

const ExistsIcon: React.FC<{ exists: boolean; fetching?: boolean }> = ({
  exists,
  fetching,
}) => {
  if (fetching) {
    return <LoadingIcon />;
  }

  if (exists) {
    return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
  }

  return <ExclamationCircleIcon className="h-5 w-5 text-gray-300" />;
};

// const Suggestion: React.FC<{
//   edge: RmProblemConnectionFragment["edges"][number];
//   mapping: RmMappingDetailsFragment;
//   field?: ResourceMetadataFieldFragment;
//   options: RmFieldLookupValueFragment[];
// }> = ({ edge, mapping, field, options }) => {
//   if (edge.node.data["problem_type"] === "unidentified_lookup") {
//     return (
//       <UnidentifiedLookupSuggestion
//         edge={edge}
//         mapping={mapping}
//         field={field}
//         options={options}
//       />
//     );
//   }
//   if (edge.node.data["problem_type"] === "local_data_not_configured") {
//     return <LocalDataNotConfiguredSuggestion edge={edge} mapping={mapping} />;
//   }

//   return <></>;
// };

// const UnidentifiedLookupSuggestion: React.FC<{
//   edge: RmProblemConnectionFragment["edges"][number];
//   mapping: RmMappingDetailsFragment;
//   field?: ResourceMetadataFieldFragment;
//   options: RmFieldLookupValueFragment[];
// }> = ({ edge, mapping, field, options }) => {
//   const { notifier } = React.useContext(NotificationContext);

//   if (edge.node.data["problem_type"] !== "unidentified_lookup") {
//     return <></>;
//   }

//   const creationIsAllowed = mapping.destinationIsLocalLookup;
//   const problem = edge.node;
//   const source = problem.data["value"];
//   const isAcknowledged = problem.acknowledgedAt != null;
//   const lookupValue = resourceMetadataLookupValueForValue(source, field);

//   const suggestions = options
//     .map((value) => {
//       return {
//         value: value.value,
//         definition: value.definition,
//         coefficient: Math.max(
//           diceCoefficient(source, value.value),
//           lookupValue ? diceCoefficient(lookupValue.name, value.value) : 0.0,
//         ),
//       };
//     })
//     .filter((suggestion) => suggestion.coefficient > 0.5);

//   suggestions.sort((a, b) => b.coefficient - a.coefficient);
//   suggestions.splice(3);

//   const [destination, setDestination] = React.useState<SelectedLookupValue>(
//     () => {
//       if (suggestions.length && suggestions[0].coefficient > 0.8) {
//         return {
//           state: "existing",
//           selectedValue: suggestions[0].value,
//           newValue: lookupValue?.name ?? source,
//         };
//       } else if (creationIsAllowed) {
//         return {
//           state: "new",
//           newValue: lookupValue?.name ?? source,
//         };
//       } else {
//         return {
//           state: "ignore",
//           newValue: "",
//         };
//       }
//     },
//   );

//   const [{ fetching }, mutation] =
//     useRmMappingSetLookupBasicTransformMutation();
//   const [, mutationAcknowledge] = useRmProblemAcknowledgeMutation();

//   const setLookup = React.useCallback(async () => {
//     if (fetching) {
//       return;
//     }
//     const { error } = await mutation({
//       mappingId: mapping.id,
//       source,
//       ...selectedValueSpread(destination),
//     }).then(notifier.notifyGraphql("Mapping updated"));
//     if (!error) {
//       mutationAcknowledge({
//         problemId: problem.id,
//       });
//     }
//   }, [
//     fetching,
//     mutation,
//     mutationAcknowledge,
//     mapping,
//     problem,
//     source,
//     destination,
//   ]);

//   return (
//     <tr>
//       <td>
//         <ResourceMetadataLookupValueComponent field={field} value={source} />
//       </td>
//       <td>
//         <LookupValueSelector
//           selected={destination}
//           setSelected={setDestination}
//           suggestions={suggestions}
//           options={options}
//           disabled={isAcknowledged}
//           creationIsAllowed={creationIsAllowed}
//         />
//         {!fetching && (
//           <button onClick={() => setLookup()} disabled={isAcknowledged}>
//             Set
//           </button>
//         )}
//         {fetching && <LoadingIcon />}
//       </td>

//       <td>
//         <ProblemStatus problem={problem} />
//       </td>
//     </tr>
//   );
// };

// const LocalDataNotConfiguredSuggestion: React.FC<{
//   edge: RmProblemConnectionFragment["edges"][number];
//   mapping: RmMappingDetailsFragment;
// }> = ({ edge, mapping }) => {
//   const problem = edge.node;
//   const fieldName = mapping.field.name;

//   if (fieldName === "LocalPropertyType") {
//     return (
//       <tr>
//         <td>
//           <strong>{problem.data["lookup_value"]}</strong> is configured on this
//           field but is not configured as an MLS local property type.
//         </td>
//         <td>
//           <Link
//             to={`/resource_mapping/mlses/${problem.rootResource.mls.id}/property_types`}
//             className="text-blue-900 hover:underline"
//           >
//             Configure property types
//           </Link>
//         </td>
//         <td>
//           <ProblemStatus problem={problem} />
//         </td>
//       </tr>
//     );
//   } else if (fieldName === "LocalStatus") {
//     return (
//       <tr>
//         <td>
//           <strong>{problem.data["lookup_value"]}</strong> is configured on this
//           field but is not configured as an MLS local status.
//         </td>
//         <td>
//           <Link
//             to={`/resource_mapping/mlses/${problem.rootResource.mls.id}/statuses`}
//             className="text-blue-900 hover:underline"
//           >
//             Configure statuses
//           </Link>
//         </td>
//         <td>
//           <ProblemStatus problem={problem} />
//         </td>
//       </tr>
//     );
//   } else {
//     return <></>;
//   }
// };

export default CanbySuggestions;
